import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { GridRowModes, DataGridPro, GridToolbarContainer, GridActionsCellItem, GridEditInputCell } from '@mui/x-data-grid-pro';
import { randomId } from '@mui/x-data-grid-generator';
import Tooltip from '@mui/material/Tooltip';
import Backdrop from '@mui/material/Backdrop';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import FileCopyIcon from '@mui/icons-material/FileCopyOutlined';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import { API_URL } from "../../../services/api-uri"
import { TextField } from '@mui/material';


function EditToolbar({ setPayments, setRowModesModel }) {

    const handleClick = () => {
        const id = randomId();
        setPayments((oldRows) => [...oldRows, { id, date: new Date(), reference: '', amount: '', isNew: true }]);
        setRowModesModel((oldModel) => ({
            ...oldModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'reference' },
        }));
    };


    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                New Payment
            </Button>
        </GridToolbarContainer>
    );
}

EditToolbar.propTypes = {
    setRowModesModel: PropTypes.func.isRequired,
    setPayments: PropTypes.func.isRequired,
};

export default function PaymentsDetail({ payments, setPayments, booking_id, comment, setComment }) {

    const [rowModesModel, setRowModesModel] = React.useState({});

    const handleRowEditStart = (params, event) => {
        event.defaultMuiPrevented = true;
    };

    const handleRowEditStop = (params, event) => {
        event.defaultMuiPrevented = true;
    };

    const handleEditClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id) => () => {
        setPayments(payments.filter((row) => row.id !== id));
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = payments.find((row) => row.id === id);
        if (editedRow.isNew) {
            setPayments(payments.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = (newRow) => {
        const updatedRow = { ...newRow, isNew: false };
        setPayments(payments.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    };

    const columns = [
        {
            field: 'date',
            headerName: 'Date Created',
            type: 'date',
            width: 180,
            editable: false,
            align: "center",
        },
        {
            field: 'reference',
            headerName: 'Reference',
            type: 'text',
            flex: 1,
            editable: true,
            renderEditCell: (params) => (
                <GridEditInputCell
                    {...params}
                    inputProps={{ maxLength: 100 }}
                />
            ),
        },
        {
            field: 'amount',
            headerName: 'Amount',
            align: "center",
            type: 'number',
            editable: true,
        },
        {
            field: 'isDeposit',
            headerName: 'Deposit',
            type: 'boolean',
            editable: true,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <>

            <Box sx={{ height: 500, width: '100%' }} >
                <DataGridPro
                    rows={payments.map((p) => {
                        p.date = new Date(p.date);
                        return p;
                    })}
                    columns={columns}
                    editMode="row"
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
                    onRowEditStart={handleRowEditStart}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={processRowUpdate}
                    components={{
                        Toolbar: EditToolbar,
                    }}
                    hideFooter
                    componentsProps={{
                        toolbar: { setPayments, setRowModesModel },
                    }}
                    experimentalFeatures={{ newEditingApi: true }}
                />
            </Box>
            <TextField
                id="outlined-multiline-flexible"
                fullWidth
                multiline
                value={comment}
                rows={3}
                onChange={(e) => setComment(e.target.value)}
                inputProps={{ maxLength: 250 }}
            />
        </>
    );
}